import { z } from 'zod';
import { localeSchema } from './common.js';

/**
 * The RS256 EMS SSO token payload, verified against the EMS JWKS by
 * `@chopchop-se/ems-auth`. Mirrors that package's `EmsUser` interface — kept
 * here as a zod schema so the API can re-validate claims it depends on and so
 * the web/Android clients share one type.
 */
export const emsUserSchema = z.object({
  sub: z.string().min(1),
  email: z.email(),
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  externalUserId: z.string().min(1),
  /** EMS emits UPPER_SNAKE keys; the API lowercases them on the way in. */
  roles: z.array(z.string()).default([]),
  system: z.string().min(1),
  iss: z.string().min(1),
  iat: z.number(),
  exp: z.number(),
  nbf: z.number().optional(),
});
export type EmsUser = z.infer<typeof emsUserSchema>;

export const sessionKindSchema = z.enum(['web', 'device']);
export type SessionKind = z.infer<typeof sessionKindSchema>;

/** Everything the SPA/app needs about "who am I" after a successful exchange. */
export const sessionUserSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  firstName: z.string(),
  lastName: z.string(),
  language: localeSchema,
  /** lowercase EMS role keys, e.g. ['lager'] */
  roleKeys: z.array(z.string()),
  isSuperAdmin: z.boolean(),
  /** Resolved permission keys (dot-namespace). Frontend uses these to hide chrome only. */
  permissions: z.array(z.string()),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const authMeResponseSchema = z.object({
  user: sessionUserSchema,
  session: z.object({
    id: z.uuid(),
    kind: sessionKindSchema,
    expiresAt: z.iso.datetime(),
  }),
});
export type AuthMeResponse = z.infer<typeof authMeResponseSchema>;

/** POST /api/auth/exchange — SPA hands over the EMS JWT, gets httpOnly cookies. */
export const authExchangeRequestSchema = z.object({
  token: z.string().min(20),
});
export type AuthExchangeRequest = z.infer<typeof authExchangeRequestSchema>;

/** POST /api/auth/device-exchange — Android hands over the EMS JWT + device identity. */
export const deviceIdentitySchema = z.object({
  serial: z.string().min(1).max(120),
  name: z.string().max(120).optional(),
  model: z.string().max(120).optional(),
  appVersion: z.string().max(60).optional(),
});
export type DeviceIdentity = z.infer<typeof deviceIdentitySchema>;

export const deviceExchangeRequestSchema = z.object({
  token: z.string().min(20),
  device: deviceIdentitySchema,
});
export type DeviceExchangeRequest = z.infer<typeof deviceExchangeRequestSchema>;

/** Device sessions are bearer-token based (no cookies on Android). */
export const deviceSessionResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** seconds until `accessToken` expires */
  expiresIn: z.number().int().positive(),
  refreshExpiresAt: z.iso.datetime(),
  deviceId: z.uuid(),
  user: sessionUserSchema,
});
export type DeviceSessionResponse = z.infer<typeof deviceSessionResponseSchema>;

/** POST /api/auth/refresh — body only used by device clients; web sends the cookie. */
export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(20).optional(),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

/** PATCH /api/auth/profile */
export const updateProfileRequestSchema = z.object({
  language: localeSchema,
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

/** Claims we put in the WMS session access JWT (HS256, SESSION_JWT_SECRET). */
export const sessionTokenClaimsSchema = z.object({
  sub: z.uuid(),
  sid: z.uuid(),
  kind: sessionKindSchema,
  did: z.uuid().nullish(),
  roles: z.array(z.string()),
  iat: z.number(),
  exp: z.number(),
});
export type SessionTokenClaims = z.infer<typeof sessionTokenClaimsSchema>;

// ---------------------------------------------------------------------------
// EMS inbound webhooks (contract mirrored from the old WMS api-gateway so the
// EMS-side WmsConnector needs no changes).
// ---------------------------------------------------------------------------

export const emsWebhookUpsertPayloadSchema = z.object({
  email: z.email(),
  firstName: z.string().default(''),
  lastName: z.string().default(''),
  /** EMS role key string, e.g. 'lager'. Absent = identity only, no role grant. */
  roleId: z.string().optional(),
  /** Forward-compat: newer EMS builds may send the full set. */
  roles: z.array(z.string()).optional(),
  externalUserId: z.string().optional(),
});
export type EmsWebhookUpsertPayload = z.infer<typeof emsWebhookUpsertPayloadSchema>;

export const emsWebhookEventSchema = z.object({
  event: z.literal('user_upsert'),
  payload: emsWebhookUpsertPayloadSchema,
});
export type EmsWebhookEvent = z.infer<typeof emsWebhookEventSchema>;

export const emsWebhookPatchSchema = z.object({
  isActive: z.boolean().optional(),
  email: z.email().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  roleId: z.string().optional(),
  roles: z.array(z.string()).optional(),
});
export type EmsWebhookPatch = z.infer<typeof emsWebhookPatchSchema>;

/** EMS treats a missing `result.user.id` as fatal — keep this shape verbatim. */
export const emsWebhookResponseSchema = z.object({
  result: z.object({
    user: z.object({
      id: z.uuid(),
      email: z.email().optional(),
    }),
  }),
});
export type EmsWebhookResponse = z.infer<typeof emsWebhookResponseSchema>;
